/**
 * ReviewsTab.tsx
 * Main container for the Reviews section in Admin Dashboard
 * Manages sub-tabs: Overview, Moderation, Settings, Widget, Import
 */

import React, { useState } from 'react';
import { UserProfile } from '../../../types';
import { BarChart3, CheckSquare, Settings, Code, Upload } from 'lucide-react';
import { OverviewStats } from './reviews/OverviewStats';
import { ModerationList } from './reviews/ModerationList';
import { DisplaySettings } from './reviews/DisplaySettings';
import { BadgeGenerator } from './reviews/BadgeGenerator';
import { ImportTool } from './reviews/ImportTool';
import { EmbedWidget } from './reviews/EmbedWidget';

interface ReviewsTabProps {
  user: UserProfile;
}

type SubTab = 'overview' | 'moderation' | 'settings' | 'widget' | 'import';

export const ReviewsTab: React.FC<ReviewsTabProps> = ({ user }) => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('overview');

  const subTabs = [
    { id: 'overview' as SubTab, label: 'Overview', icon: BarChart3 },
    { id: 'moderation' as SubTab, label: 'Moderation', icon: CheckSquare },
    { id: 'settings' as SubTab, label: 'Display Settings', icon: Settings },
    { id: 'widget' as SubTab, label: 'Embed Widget', icon: Code },
    { id: 'import' as SubTab, label: 'Import Reviews', icon: Upload },
  ];

  return (
    <div className="space-y-6">
      {/* Sub-navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                  activeSubTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Sub-tab Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {activeSubTab === 'overview' && <OverviewStats user={user} />}
        {activeSubTab === 'moderation' && <ModerationList user={user} />}
        {activeSubTab === 'settings' && <DisplaySettings user={user} />}
        {activeSubTab === 'widget' && <EmbedWidget user={user} />}
        {activeSubTab === 'import' && <ImportTool user={user} />}
      </div>
    </div>
  );
};
